import {
  PokemonCard,
  PokemonAttack,
  PokemonWeakness,
} from "modules/cards/model/card";
import { capitalizeFirstLetter } from "utils/presentation";
import { PokemonEnergyIcon } from "modules/common/components/CardDetailsDialog/PokemonEnergyIcon";
import { ReplacedSymbolsOracleText } from "modules/common/components/ManaFontSymbols";

interface PokemonCardInfoProps {
  card: PokemonCard;
}

export function PokemonCardInfo({ card }: PokemonCardInfoProps) {
  const { features } = card;
  const isPokemon = features.category === "Pokemon";

  return (
    <div className="flex flex-col gap-3">
      {/* Header: Name, HP, Types */}
      <div className="mt-2 md:mt-0">
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-primary text-xl font-semibold">
            {card.name}
            {features.suffix && (
              <span className="text-secondary ml-1 text-base font-normal">
                {features.suffix}
              </span>
            )}
          </h1>
          {isPokemon && features.hp > 0 && (
            <span className="text-base-content whitespace-nowrap text-lg font-bold">
              HP {features.hp}
            </span>
          )}
        </div>

        {/* Type badges */}
        {features.types.length > 0 && (
          <div className="mt-1 flex items-center gap-1.5">
            {features.types.map((type) => (
              <span key={type} className="flex items-center gap-1">
                <PokemonEnergyIcon type={type} />
                <span className="text-secondary text-sm">{type}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Stage & Evolution */}
      {isPokemon && (features.stage || features.evolveFrom) && (
        <p className="text-secondary text-sm">
          {features.stage && capitalizeFirstLetter(features.stage)}
          {features.evolveFrom && (
            <span>
              {features.stage ? " — " : ""}Evolves from{" "}
              <span className="text-base-content font-medium">
                {features.evolveFrom}
              </span>
            </span>
          )}
        </p>
      )}

      {/* Card type line */}
      <p className="text-secondary text-sm">{card.type}</p>

      {/* Rules / Abilities */}
      {features.rules && features.rules.length > 0 && (
        <div className="border-base-300 space-y-2 border-t pt-2">
          {features.rules.map((rule, idx) => (
            <ReplacedSymbolsOracleText
              key={idx}
              oracleText={rule}
              className="text-base-content text-sm italic"
            />
          ))}
        </div>
      )}

      {/* Attacks */}
      {features.attacks && features.attacks.length > 0 && (
        <div className="border-base-300 space-y-3 border-t pt-2">
          {features.attacks.map((attack, idx) => (
            <AttackRow key={idx} attack={attack} />
          ))}
        </div>
      )}

      {/* Footer: Weakness, Retreat, Regulation */}
      {isPokemon && (
        <div className="border-base-300 flex flex-wrap items-center gap-x-4 gap-y-1 border-t pt-2 text-sm">
          <WeaknessDisplay weaknesses={features.weaknesses} />
          <RetreatDisplay retreat={features.retreat} />
          {features.regulationMark && (
            <span className="text-secondary">
              Regulation:{" "}
              <span className="badge badge-sm badge-outline">
                {features.regulationMark}
              </span>
            </span>
          )}
        </div>
      )}

      {/* Description (Pokedex flavor text) */}
      {features.description && (
        <ReplacedSymbolsOracleText
          oracleText={features.description}
          className="text-secondary text-sm italic"
        />
      )}

      {/* Illustrator */}
      {card.artist && (
        <p className="text-secondary text-xs">
          Illus. {card.artist}
          {card.number && <span className="ml-2">#{card.number}</span>}
        </p>
      )}
    </div>
  );
}

function AttackRow({ attack }: { attack: PokemonAttack }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          {/* Energy cost icons */}
          {attack.cost.map((energy, idx) => (
            <PokemonEnergyIcon
              key={idx}
              type={energy}
              className="h-4 w-4 text-[9px]"
            />
          ))}
          <span className="text-base-content ml-1 font-medium">
            {attack.name}
          </span>
        </div>
        {attack.damage && (
          <span className="text-base-content text-lg font-bold">
            {attack.damage}
          </span>
        )}
      </div>
      {attack.effect && (
        <ReplacedSymbolsOracleText
          oracleText={attack.effect}
          className="text-secondary mt-0.5 text-sm"
        />
      )}
    </div>
  );
}

function WeaknessDisplay({ weaknesses }: { weaknesses?: PokemonWeakness[] }) {
  if (!weaknesses || weaknesses.length === 0) return null;

  return (
    <span className="flex items-center gap-1">
      <span className="text-secondary">Weakness:</span>
      {weaknesses.map((w, idx) => (
        <span key={idx} className="flex items-center gap-0.5">
          <PokemonEnergyIcon type={w.type} className="h-4 w-4 text-[9px]" />
          <span className="text-base-content text-xs font-medium">
            {w.value}
          </span>
        </span>
      ))}
    </span>
  );
}

function RetreatDisplay({ retreat }: { retreat: number }) {
  if (retreat === 0) return null;

  return (
    <span className="flex items-center gap-1">
      <span className="text-secondary">Retreat:</span>
      {Array.from({ length: retreat }).map((_, idx) => (
        <PokemonEnergyIcon
          key={idx}
          type="Colorless"
          className="h-4 w-4 text-[9px]"
        />
      ))}
    </span>
  );
}
